import React, { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Shield,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Check,
  AlertTriangle,
  Phone,
  BadgeCheck,
  Briefcase,
  Users,
  Copy,
} from 'lucide-react';
import { getThemeColor } from '../config/appConfig';
import NewtonsCradleLoader from '../components/common/NewtonsCradleLoader';
import apiService from '../utils/apiService';
import {
  formatProfileDate,
  formatProfilePhone,
  parseUserProfile,
  profileToEditForm,
} from '../utils/userProfile';
import { useFetchOnceByKey } from '../hooks/useMountOnce';

const THEME = getThemeColor('primary');

const inputCls =
  'w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-red-800 focus:ring-2 focus:ring-red-800/15';

const SectionLoader = ({ className = 'py-10' }) => (
  <div className={`flex items-center justify-center ${className}`}>
    <NewtonsCradleLoader size="28px" />
  </div>
);

const VerificationBadge = ({ verified, label }) =>
  verified ? (
    <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
      Verified
    </span>
  ) : (
    <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
      {label || 'Not Verified'}
    </span>
  );

const StatusBadge = ({ status }) => {
  const value = (status || 'Pending').toString();
  const lower = value.toLowerCase();
  const cls =
    lower === 'approved'
      ? 'text-emerald-700 bg-emerald-50'
      : lower === 'pending'
        ? 'text-amber-700 bg-amber-50'
        : 'text-gray-700 bg-gray-100';
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{value}</span>
  );
};

const AlertMessage = ({ type, message }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div
      className={`mb-4 flex items-center gap-2 rounded-xl px-4 py-3 text-sm ${
        isError ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
      }`}
    >
      {isError ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <Check className="w-4 h-4 shrink-0" />}
      <span>{message}</span>
    </div>
  );
};

const ProfileStatCard = ({ title, value, icon: Icon, editable, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
    <div className="flex items-center gap-3.5 sm:gap-4">
      <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
        <Icon className="w-6 h-6" style={{ color: THEME }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500 leading-tight">{title}</p>
        {editable ? (
          children
        ) : (
          <p className="text-xl sm:text-[22px] font-bold text-gray-900 leading-tight mt-1 tracking-tight">
            {value || '—'}
          </p>
        )}
      </div>
    </div>
  </div>
);

const ProfileField = ({ label, value, editable, children, badge }) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-2">
      <p className="text-xs text-gray-500">{label}</p>
      {badge}
    </div>
    {editable ? children : <p className="text-sm font-semibold text-gray-900 break-words">{value || '—'}</p>}
  </div>
);

const PasswordField = ({ label, value, onChange, visible, onToggle, placeholder }) => (
  <div>
    <label className="block text-xs text-gray-500 mb-1.5">{label}</label>
    <div className="relative">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        className={`${inputCls} pr-10`}
        placeholder={placeholder}
        required
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  </div>
);

const Profile = () => {
  const location = useLocation();
  const isSecurityPage = location.pathname === '/settings/security';
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileUpdateMessage, setProfileUpdateMessage] = useState('');
  const [profileUpdateError, setProfileUpdateError] = useState('');
  const [editFormData, setEditFormData] = useState({
    state: '',
    city: '',
    zipCode: '',
    address: '',
    lastCompanyName: '',
    totalClients: '',
    totalTradingExp: '',
    dateOfBirth: '',
  });
  const [copySuccess, setCopySuccess] = useState(false);
  const [affiliateCodeCopied, setAffiliateCodeCopied] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordUpdateMessage, setPasswordUpdateMessage] = useState('');
  const [passwordUpdateError, setPasswordUpdateError] = useState('');

  const fetchUserProfile = useCallback(async ({ cancelled }) => {
    try {
      setProfileLoading(true);
      const response = await apiService.getUserProfile();
      if (cancelled()) return;
      if (response.success && response.data) {
        const profile = parseUserProfile(response.data);
        setProfileData(profile);
        setEditFormData(profileToEditForm(profile));
      }
    } catch {
      /* keep defaults */
    } finally {
      if (!cancelled()) setProfileLoading(false);
    }
  }, []);

  useFetchOnceByKey('user-profile', fetchUserProfile, true);

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileUpdateMessage('');
    setProfileUpdateError('');

    const requiredFields = [
      { field: 'state', label: 'State' },
      { field: 'city', label: 'City' },
      { field: 'zipCode', label: 'Zip Code' },
      { field: 'address', label: 'Address' },
      { field: 'lastCompanyName', label: 'Last Company Name' },
      { field: 'totalClients', label: 'Total Clients' },
      { field: 'totalTradingExp', label: 'Trading Experience' },
    ];

    const emptyFields = requiredFields.filter(
      (item) => !editFormData[item.field] || editFormData[item.field].trim() === ''
    );

    if (emptyFields.length > 0) {
      setProfileUpdateError(`Please fill in all required fields: ${emptyFields.map((f) => f.label).join(', ')}`);
      return;
    }

    setIsUpdatingProfile(true);

    try {
      const response = await apiService.updateUserProfile(editFormData);
      if (response.success) {
        setProfileUpdateMessage('Profile updated successfully!');

        try {
          const profileResponse = await apiService.getUserProfile();
          if (profileResponse.success && profileResponse.data) {
            const profile = parseUserProfile(profileResponse.data);
            setProfileData(profile);
            setEditFormData(profileToEditForm(profile));
          }
        } catch {
          setProfileData((prev) => (prev ? { ...prev, ...editFormData } : prev));
        }

        setTimeout(() => setProfileUpdateMessage(''), 3000);
      } else {
        setProfileUpdateError(response.message || 'Failed to update profile');
      }
    } catch (error) {
      setProfileUpdateError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordUpdateMessage('');
    setPasswordUpdateError('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordUpdateError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordUpdateError('New password must be at least 6 characters long');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      const response = await apiService.updatePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword
      );

      if (response.success) {
        setPasswordUpdateMessage('Password updated successfully!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordForm(false);
        setTimeout(() => setPasswordUpdateMessage(''), 3000);
      }
    } catch (error) {
      setPasswordUpdateError(error.message || 'Failed to update password. Please try again.');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleCopyPartnerUrl = async () => {
    if (!profileData?.partnerUrl) return;
    try {
      await navigator.clipboard.writeText(profileData.partnerUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      setCopySuccess(false);
    }
  };

  const handleCopyAffiliateCode = async () => {
    const code = profileData?.affiliateCode?.toString().trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setAffiliateCodeCopied(true);
      setTimeout(() => setAffiliateCodeCopied(false), 2000);
    } catch {
      setAffiliateCodeCopied(false);
    }
  };

  const isEditable = profileData && !profileData.isAccountVerified;
  const pageTitle = isSecurityPage ? 'Security' : 'Profile';
  const pageSubtitle = isSecurityPage
    ? 'Manage your password and account security'
    : 'Manage your profile information';

  const renderProfileContent = () => {
    if (profileLoading) {
      return (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <SectionLoader />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-5">
                <SectionLoader className="py-6" />
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (!profileData) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-red-600" />
          <p className="text-sm font-semibold text-gray-900">Failed to load profile data</p>
          <p className="text-sm text-gray-500 mt-1">Please try refreshing the page</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h2 className="text-xl font-bold text-gray-900">{profileData.fullName}</h2>
            <StatusBadge status={profileData.status} />
          </div>
          <div className="space-y-1.5 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-2">
              <Mail className="w-4 h-4 shrink-0" style={{ color: THEME }} />
              <span>{profileData.email || '—'}</span>
              <VerificationBadge verified={profileData.isEmailVerified} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Phone className="w-4 h-4 shrink-0" style={{ color: THEME }} />
              <span>{formatProfilePhone(profileData.countryCode, profileData.mobileNumber)}</span>
              <VerificationBadge verified={profileData.isNumberVerified} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ProfileStatCard title="Total Clients" value={profileData.totalClients} icon={Users} editable={isEditable}>
            <input
              type="text"
              value={editFormData.totalClients}
              onChange={(e) => handleEditFormChange('totalClients', e.target.value)}
              className={`${inputCls} mt-1 font-bold`}
              placeholder="Enter total clients"
              required
            />
          </ProfileStatCard>
          <ProfileStatCard
            title="Trading Experience"
            value={profileData.totalTradingExp}
            icon={Briefcase}
            editable={isEditable}
          >
            <input
              type="text"
              value={editFormData.totalTradingExp}
              onChange={(e) => handleEditFormChange('totalTradingExp', e.target.value)}
              className={`${inputCls} mt-1 font-bold`}
              placeholder="Enter trading experience"
              required
            />
          </ProfileStatCard>
          <ProfileStatCard title="Account Status" value={profileData.status || 'Pending'} icon={BadgeCheck} />
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4">Partner Information</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <p className="text-xs text-gray-500">Partner Code</p>
              <div className="flex items-center gap-2 min-w-0">
                <p className="text-sm font-semibold text-gray-900 break-all">
                  {profileData.affiliateCode || '—'}
                </p>
                <button
                  type="button"
                  onClick={handleCopyAffiliateCode}
                  disabled={!profileData.affiliateCode}
                  className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition disabled:opacity-40 disabled:pointer-events-none"
                  aria-label="Copy partner code"
                  title={affiliateCodeCopied ? 'Copied' : 'Copy partner code'}
                >
                  {affiliateCodeCopied ? (
                    <Check className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                  ) : (
                    <Copy className="w-4 h-4" strokeWidth={1.75} />
                  )}
                </button>
              </div>
              {affiliateCodeCopied ? (
                <p className="text-xs text-emerald-600 font-medium">Copied to clipboard!</p>
              ) : null}
            </div>
            <ProfileField label="Member Since" value={formatProfileDate(profileData.createdDateTime)} />
            <div className="lg:col-span-2">
              <p className="text-xs text-gray-500 mb-1.5">Partner URL</p>
              <div
                className="flex items-center rounded-xl border bg-white overflow-hidden"
                style={{ borderColor: '#fecaca' }}
              >
                <span className="flex-1 min-w-0 text-sm font-medium truncate px-4 py-3" style={{ color: THEME }}>
                  {profileData.partnerUrl || '—'}
                </span>
                <button
                  type="button"
                  onClick={handleCopyPartnerUrl}
                  disabled={!profileData.partnerUrl}
                  className="shrink-0 inline-flex items-center gap-2 mx-2 px-3 py-2 rounded-lg border-2 text-sm font-semibold hover:bg-red-50 transition disabled:opacity-50"
                  style={{ borderColor: THEME, color: THEME }}
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
              </div>
              {copySuccess && (
                <p className="text-xs text-emerald-600 font-medium mt-2">Copied to clipboard!</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <ProfileField label="Full Name" value={profileData.fullName} />
            <ProfileField
              label="Date of Birth"
              value={formatProfileDate(profileData.dateOfBirth)}
              editable={isEditable}
            >
              <input
                type="date"
                value={editFormData.dateOfBirth}
                onChange={(e) => handleEditFormChange('dateOfBirth', e.target.value)}
                className={inputCls}
                max={new Date().toISOString().slice(0, 10)}
              />
            </ProfileField>
            <ProfileField label="Last Company" value={profileData.lastCompanyName} editable={isEditable}>
              <input
                type="text"
                value={editFormData.lastCompanyName}
                onChange={(e) => handleEditFormChange('lastCompanyName', e.target.value)}
                className={inputCls}
                placeholder="Enter company name"
                required
              />
            </ProfileField>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4">Contact & Address</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <ProfileField
              label="Email Address"
              value={profileData.email}
              badge={<VerificationBadge verified={profileData.isEmailVerified} />}
            />
            <ProfileField
              label="Mobile Number"
              value={formatProfilePhone(profileData.countryCode, profileData.mobileNumber)}
              badge={<VerificationBadge verified={profileData.isNumberVerified} />}
            />
            <ProfileField label="Country" value={profileData.countryName} />
            <ProfileField label="State" value={profileData.state} editable={isEditable}>
              <input
                type="text"
                value={editFormData.state}
                onChange={(e) => handleEditFormChange('state', e.target.value)}
                className={inputCls}
                placeholder="Enter state"
                required
              />
            </ProfileField>
            <ProfileField label="City" value={profileData.city} editable={isEditable}>
              <input
                type="text"
                value={editFormData.city}
                onChange={(e) => handleEditFormChange('city', e.target.value)}
                className={inputCls}
                placeholder="Enter city"
                required
              />
            </ProfileField>
            <ProfileField label="Zip Code" value={profileData.zipCode} editable={isEditable}>
              <input
                type="text"
                value={editFormData.zipCode}
                onChange={(e) => handleEditFormChange('zipCode', e.target.value)}
                className={inputCls}
                placeholder="Enter zip code"
                required
              />
            </ProfileField>
            <div className="sm:col-span-2 lg:col-span-3">
              <ProfileField label="Address" value={profileData.address} editable={isEditable}>
                <input
                  type="text"
                  value={editFormData.address}
                  onChange={(e) => handleEditFormChange('address', e.target.value)}
                  className={inputCls}
                  placeholder="Enter address"
                  required
                />
              </ProfileField>
            </div>
          </div>
        </div>

        {isEditable && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
            <AlertMessage type="success" message={profileUpdateMessage} />
            <AlertMessage type="error" message={profileUpdateError} />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-base font-bold text-gray-900">Save Your Changes</h3>
                <p className="text-sm text-gray-500 mt-0.5">Complete your profile to verify your account</p>
              </div>
              <button
                type="button"
                onClick={handleProfileUpdate}
                disabled={isUpdatingProfile}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition disabled:opacity-50"
                style={{ backgroundColor: THEME }}
              >
                {isUpdatingProfile ? <NewtonsCradleLoader size="18px" color="#fff" /> : <Check className="w-4 h-4" />}
                {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSecurityContent = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
            <Shield className="w-5 h-5" style={{ color: THEME }} />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Security Status</h3>
            <p className="text-sm text-gray-500">Your account security overview</p>
          </div>
        </div>
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4" style={{ color: THEME }} />
            <span className="text-sm font-semibold text-gray-900">Password</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">Keep your password strong and unique</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">Change Password</h3>
            <p className="text-sm text-gray-500 mt-0.5">Update your account password</p>
          </div>
          {!showPasswordForm && (
            <button
              type="button"
              onClick={() => setShowPasswordForm(true)}
              className="px-4 py-2 rounded-xl border-2 text-sm font-semibold hover:bg-red-50 transition"
              style={{ borderColor: THEME, color: THEME }}
            >
              Change Password
            </button>
          )}
        </div>

        {showPasswordForm && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4 border-t border-gray-100 pt-4">
            <AlertMessage type="success" message={passwordUpdateMessage} />
            <AlertMessage type="error" message={passwordUpdateError} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PasswordField
                label="Current Password"
                value={passwordForm.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                visible={showPasswords.current}
                onToggle={() => togglePasswordVisibility('current')}
                placeholder="Current password"
              />
              <PasswordField
                label="New Password"
                value={passwordForm.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                visible={showPasswords.new}
                onToggle={() => togglePasswordVisibility('new')}
                placeholder="New password"
              />
              <PasswordField
                label="Confirm Password"
                value={passwordForm.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                visible={showPasswords.confirm}
                onToggle={() => togglePasswordVisibility('confirm')}
                placeholder="Confirm password"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={isUpdatingPassword}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME }}
              >
                {isUpdatingPassword ? <NewtonsCradleLoader size="18px" color="#fff" /> : null}
                {isUpdatingPassword ? 'Updating...' : 'Update Password'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setPasswordUpdateMessage('');
                  setPasswordUpdateError('');
                }}
                disabled={isUpdatingPassword}
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return (
    <div className="px-4 sm:px-6 py-5 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
        <p className="text-sm text-gray-500 mt-1">
          <Link to="/" className="hover:text-red-800">
            Dashboard
          </Link>
          <span className="mx-1.5">›</span>
          <span>{pageSubtitle}</span>
        </p>
      </div>

      {isSecurityPage ? renderSecurityContent() : renderProfileContent()}
    </div>
  );
};

export default Profile;
